// Generated by ts-to-zod
import { z } from "zod";
import { componentCategorySchema } from '@smartphone-tycoon/shared-core';

export const productMetadataSchema = z.object({
  companyId: z.string(),
  designDate: z.object({
    day: z.number(),
    month: z.number(),
    year: z.number(),
  }),
  releaseDate: z
    .object({
      day: z.number(),
      month: z.number(),
      year: z.number(),
    })
    .optional(),
  discontinuedDate: z
    .object({
      day: z.number(),
      month: z.number(),
      year: z.number(),
    })
    .optional(),
  isReleased: z.boolean().optional(),
});

export const productSalesMetricsSchema = z.object({
  unitsSold: z.number(),
  revenue: z.number(),
  averageRating: z.number(),
  marketShare: z.number(),
});

export const productReviewSchema = z.object({
  rating: z.number().min(0).max(5),
  text: z.string(),
  author: z.string(),
  publication: z.string().optional(),
  reviewerTitle: z.string().optional(),
});

export const productReviewsSchema = z.object({
  professional: z.array(productReviewSchema),
  customer: z.array(productReviewSchema),
});

export const productValidationErrorSchema = z.object({
  field: z.string(),
  message: z.string(),
});

export const developmentPhaseSchema = z.union([
  z.literal("design"),
  z.literal("engineering"),
  z.literal("testing"),
  z.literal("manufacturing"),
]);

export const productDevelopmentPhaseSchema = z.object({
  phase: developmentPhaseSchema,
  startDate: z.object({
    day: z.number(),
    month: z.number(),
    year: z.number(),
  }),
  estimatedDuration: z.number(),
  actualDuration: z.number().optional(),
  budget: z.number(),
  confidence: z.number(),
  qualityScore: z.number(),
  riskEvents: z.array(z.string()),
  investmentCount: z.number().default(0),
});

export const investmentStateSchema = z.object({
  type: z.enum(['minor', 'major']).optional(),
  strikeCount: z.number().default(0),
  maxStrikes: z.number().default(3),
  lastUpdateTime: z.date().optional(),
});

export const phasePreferencesSchema = z.object({
  design_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask'),
  engineering_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask'),
  testing_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask'),
  manufacturing_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask'),
});

export const productPreferenceStateSchema = z.object({
  capturedPreferences: phasePreferencesSchema,
  capturedAt: z.date(),
  lastPhaseProcessed: z.date().optional(),
  investmentStates: z.object({
    design: investmentStateSchema.optional(),
    engineering: investmentStateSchema.optional(),
    testing: investmentStateSchema.optional(),
    manufacturing: investmentStateSchema.optional(),
  }).default({}),
});

export const productDesignSchema = z.object({
  name: z.string(),
  price: z.number(),
  color: z.string(),
  material: z.string(),
  components: z.record(componentCategorySchema.shape.id, z.string()),
  design: z.object({
    buttonColor: z.string(),
    hasButtons: z.boolean(),
    screenBorderColor: z.string(),
    cameraStyle: z.string(),
    cameraColor: z.string(),
    cameraPosition: z.object({
      x: z.number(),
      y: z.number(),
    }),
    buttonPositions: z.object({
      volume: z.number(),
      power: z.number(),
    }),
    phoneSize: z.object({
      width: z.number(),
      height: z.number(),
      depth: z.number(),
    }),
    screenSize: z.object({
      width: z.number(),
      height: z.number(),
    }),
    cornerRadius: z.number(),
  }),
  techScore: z.number(),
  marketAppeal: z.number(),
});

export const productSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  developmentCost: z.number(),
  productionCost: z.number(),
  price: z.number(),
  techScore: z.number(),
  marketAppeal: z.number(),
  status: z.union([
    z.literal("concept"), // Legacy status, will be migrated to "design"
    z.literal("development"),
    z.literal("design"),
    z.literal("engineering"),
    z.literal("testing"),
    z.literal("manufacturing"),
    z.literal("production"),
    z.literal("released"),
    z.literal("discontinued"),
  ]),
  designConfig: productDesignSchema,
  metadata: productMetadataSchema,
  salesMetrics: productSalesMetricsSchema,
  reviews: productReviewsSchema.optional(),
  developmentPhase: productDevelopmentPhaseSchema.optional(),
  preferenceState: productPreferenceStateSchema.optional(),
});

export const productOperationResultSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  data: productSchema.optional(),
});
